/* eslint-disable @wyz/module/no-relative-import */
import { IOptionItem, TFunction } from '@wyz/types';
import {
  DEFAULT_EURO_SIGN,
  DateUtils,
  getCurrencySymbol,
  invariant,
} from '@wyz/utils';
import { get, isNil, set, uniq } from 'lodash-es';
import {
  IBrand,
  ICustomer,
  ISeasonOptionItem,
  ISupplierTypeOptionItem,
  IVehicleTypeGroupList,
  IVehicleTypeGroupsOptionItem,
  TranslationKeys,
} from '~/common/types';
import {
  ICreateQuotePricingPolicyPayload,
  IDiameter,
  IGetOneQuotePricingPolicy,
  IGlobalDefaultQuotePricingFormData,
  IGlobalQuotePricingFormData,
  IPolicyDefinitionValue,
  IPricingStatus,
  IQuotePolicyItem,
  IQuotePricingConfigurationStepForm,
  IQuotePricingSectionPayload,
  QuotePricingPolicyFormStep,
} from '~/purchase/types';

import { DEFAULT_VEHICLE_TYPE } from '~/purchase/helpers';
import {
  CUSTOMER_CRITERIA_CODE,
  NO_REFINEMENT_VALUE,
  buildVehicleTypeLookup,
  getDiameterPayload,
  getLookupValue,
} from './common';

// Dev's note : The order is very important, we use this array as data source for the UI Stepper
export const QUOTE_PRICING_POLICY_STEP_LABELS: Record<
  QuotePricingPolicyFormStep,
  keyof TranslationKeys
> = {
  PRICING: 'app.quote.pricing_policy.form.step1.wizard_title',
  APPLICATION: 'app.quote.pricing_policy.form.step3.wizard_title',
};
export const QUOTE_PRICING_POLICY_STEP_INDEX: Record<
  QuotePricingPolicyFormStep,
  number
> = {
  APPLICATION: 0,
  PRICING: 1,
} as const;
export const FIRST_STEP = Math.min(
  ...Object.values(QUOTE_PRICING_POLICY_STEP_INDEX),
);
export const LAST_STEP = Math.max(
  ...Object.values(QUOTE_PRICING_POLICY_STEP_INDEX),
);

export const hasRefined = (item: IQuotePolicyItem) => {
  return (
    item.refinementFlags?.diameter ||
    item.refinementFlags?.season ||
    item.refinementFlags?.supplierType ||
    item.refinementFlags?.tyreType
  );
};

export const getSeasons = (
  allSeason: Array<ISeasonOptionItem>,
  flags: IQuotePolicyItem['refinementFlags'],
): Array<ISeasonOptionItem> => {
  if (flags.season) {
    return allSeason;
  }
  return [{ label: NO_REFINEMENT_VALUE, value: -1 }];
};

export const getTyreTypes = (
  t: TFunction<TranslationKeys>,
  allVehicleTypeGroups: IVehicleTypeGroupList['data'],
  selectedVehicleTypeGroup: string,
  flags: IQuotePolicyItem['refinementFlags'],
): Array<IOptionItem> => {
  if (flags.tyreType) {
    return (
      allVehicleTypeGroups
        .find((item) => item.publicKey === selectedVehicleTypeGroup)
        ?.vehicleTypes.map((tyreType) => ({
          label: t(tyreType.i18nKey),
          value: tyreType.publicKey,
        })) ?? []
    );
  }
  return [{ label: '', value: NO_REFINEMENT_VALUE }];
};

const buildLookups = (
  seasons: Array<ISeasonOptionItem>,
  supplierTypes: Array<ISupplierTypeOptionItem>,
  brands: Array<IBrand>,
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
) => {
  const brandsLookup = brands.reduce(
    (acc, curr) => {
      acc[String(curr.id)] = { key: String(curr.id), name: curr.name };
      return acc;
    },
    {} as Record<string, IPolicyDefinitionValue>,
  );
  const seasonsLookup = seasons.reduce(
    (acc, curr) => {
      acc[curr.label] = { key: String(curr.value), name: curr.label };
      return acc;
    },
    {} as Record<string, IPolicyDefinitionValue>,
  );
  const supplierTypesLookup = supplierTypes.reduce(
    (acc, curr) => {
      acc[curr.value] = { key: String(curr.id), name: curr.label };
      return acc;
    },
    {} as Record<string, IPolicyDefinitionValue>,
  );

  return {
    brandsLookup,
    seasonsLookup,
    supplierTypesLookup,
    ...buildVehicleTypeLookup(vehicleTypeGroups),
  };
};

export const formToPayload = (
  formData: IGlobalQuotePricingFormData,
  status: IPricingStatus,
  customerId: string,
  seasons: Array<ISeasonOptionItem>,
  supplierTypes: Array<ISupplierTypeOptionItem>,
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
  brands: Array<IBrand>,
): ICreateQuotePricingPolicyPayload => {
  invariant(formData.PRICING);
  invariant(formData.APPLICATION);
  const {
    brandsLookup,
    seasonsLookup,
    supplierTypesLookup,
    vehicleTypeGroupLookup,
    vehicleTypeLookup,
  } = buildLookups(seasons, supplierTypes, brands, vehicleTypeGroups);
  return {
    name: formData.PRICING.name,
    vehicleTypeGroup: getLookupValue(
      formData.PRICING.vehicleTypeGroup,
      vehicleTypeGroupLookup,
    ),
    startDate: DateUtils.formatTimestamp(formData.APPLICATION?.startDate, {
      customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
    }),
    endDate: formData.APPLICATION?.endDate
      ? DateUtils.formatTimestamp(formData.APPLICATION?.endDate, {
          customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
        })
      : undefined,
    status: status,
    pricingPolicyType: 'quote',
    criteria: [
      {
        criterion: '',
        id: '',
        code: CUSTOMER_CRITERIA_CODE,
        value: customerId,
      },
    ],
    sections: flattenRefinements(
      formData.PRICING.configurations,
      brandsLookup,
      seasonsLookup,
      supplierTypesLookup,
      vehicleTypeLookup,
    ),
    default: false,
  };
};

export const formToPayloadForDefault = (
  formData: IGlobalDefaultQuotePricingFormData,
  status: IPricingStatus,
  seasons: Array<ISeasonOptionItem>,
  supplierTypes: Array<ISupplierTypeOptionItem>,
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
  brands: Array<IBrand>,
): ICreateQuotePricingPolicyPayload => {
  invariant(formData.PRICING);
  invariant(formData.APPLICATION);
  const {
    brandsLookup,
    seasonsLookup,
    supplierTypesLookup,
    vehicleTypeGroupLookup,
    vehicleTypeLookup,
  } = buildLookups(seasons, supplierTypes, brands, vehicleTypeGroups);
  return {
    name: formData.PRICING.name,
    vehicleTypeGroup: getLookupValue(
      formData.PRICING.vehicleTypeGroup,
      vehicleTypeGroupLookup,
    ),
    startDate: DateUtils.formatTimestamp(formData.APPLICATION?.startDate, {
      customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
    }),
    endDate: formData.APPLICATION?.endDate
      ? DateUtils.formatTimestamp(formData.APPLICATION?.endDate, {
          customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
        })
      : undefined,
    status: status,
    pricingPolicyType: 'quote',
    default: true,
    criteria: [
      ...formData.APPLICATION.customers.map((customerId: string) => ({
        criterion: '',
        id: '',
        code: CUSTOMER_CRITERIA_CODE,
        value: customerId,
      })),
    ],
    sections: flattenRefinements(
      formData.PRICING.configurations,
      brandsLookup,
      seasonsLookup,
      supplierTypesLookup,
      vehicleTypeLookup,
    ),
  };
};

export const flattenRefinements = (
  configurations: IQuotePricingConfigurationStepForm['configurations'],
  brandsLookup: Record<string, IPolicyDefinitionValue>,
  seasonsLookup: Record<string, IPolicyDefinitionValue>,
  supplierTypesLookup: Record<string, IPolicyDefinitionValue>,
  vehicleTypeLookup: Record<string, IPolicyDefinitionValue>,
): IQuotePricingSectionPayload[] => {
  const sections: IQuotePricingSectionPayload[] = [];
  configurations.forEach((configuration, configurationIndex) => {
    const supplierTypes: Array<IPolicyDefinitionValue> =
      configuration.supplierTypes.map((codename) =>
        getLookupValue(codename, supplierTypesLookup),
      );
    sections.push({
      configurationType: configuration.configurationType,
      brands: configuration.brands.map((brandId) =>
        getLookupValue(String(brandId), brandsLookup),
      ),
      name: configuration.name,
      policies: [],
      supplierTypes: supplierTypes.map((item) => Number(item.key)),
    });
    if (!hasRefined(configuration)) {
      invariant(!isNil(configuration.configurationTypeValue));
      sections[configurationIndex]['policies'].push({
        diameter: null,
        priceValue: {
          c: configuration.configurationTypeValue,
          cg1: 0,
          cg2: 0,
          cg3: 0,
          cg4: 0,
          cg5: 0,
        },
        season: null,
        tyreType: null,
        supplierType: null,
      });
    } else {
      Object.keys(configuration.definition?.supplierTypes ?? {}).forEach(
        (supplierTypeCodename) => {
          const refinements =
            configuration.definition?.supplierTypes[supplierTypeCodename]
              .refinements || [];
          refinements.forEach((refinement) => {
            Object.entries(refinement?.seasons ?? {}).forEach(
              ([seasonLabel, tyreTypes]) => {
                Object.entries(tyreTypes.tyreTypes).forEach(
                  ([tyreTypeKey, value]) => {
                    const diameter = getDiameterPayload(refinement?.diameter);
                    const tyreType =
                      tyreTypeKey === NO_REFINEMENT_VALUE
                        ? null
                        : getLookupValue(tyreTypeKey, vehicleTypeLookup, false);
                    const supplierType =
                      supplierTypeCodename === NO_REFINEMENT_VALUE
                        ? null
                        : getLookupValue(
                            supplierTypeCodename,
                            supplierTypesLookup,
                            false,
                          );
                    if (tyreType !== undefined && supplierType !== undefined) {
                      sections[configurationIndex]['policies'].push({
                        diameter: diameter,
                        priceValue: {
                          c: value.value,
                          cg1: 0,
                          cg2: 0,
                          cg3: 0,
                          cg4: 0,
                          cg5: 0,
                        },
                        season:
                          seasonLabel === NO_REFINEMENT_VALUE
                            ? null
                            : getLookupValue(seasonLabel, seasonsLookup),
                        tyreType,
                        supplierType,
                      });
                    }
                  },
                );
              },
            );
          });
        },
      );
    }
  });

  return sections;
};

type InitialValuesOptions = {
  vehicleTypeGroups: Array<IVehicleTypeGroupsOptionItem>;
};

export const getInitialValues = ({
  vehicleTypeGroups,
}: InitialValuesOptions): IGlobalQuotePricingFormData => {
  const QUOTE_PRICING_POLICY_DEFAULT_VALUES: IGlobalQuotePricingFormData = {
    PRICING: {
      configurations: [],
      name: '',
      vehicleTypeGroup:
        vehicleTypeGroups.find((item) => item.name === DEFAULT_VEHICLE_TYPE)
          ?.value ?? '',
    },
    APPLICATION: undefined,
  };
  return {
    ...QUOTE_PRICING_POLICY_DEFAULT_VALUES,
  };
};

// get initial values for default quote
export const getInitialValuesForDefaultPricingPolicy = ({
  vehicleTypeGroups,
}: InitialValuesOptions): IGlobalDefaultQuotePricingFormData => {
  const QUOTE_PRICING_POLICY_DEFAULT_VALUES: IGlobalDefaultQuotePricingFormData =
    {
      PRICING: {
        configurations: [],
        name: '',
        vehicleTypeGroup:
          vehicleTypeGroups.find((item) => item.name === DEFAULT_VEHICLE_TYPE)
            ?.value ?? '',
      },
      APPLICATION: {
        customers: [],
        startDate: undefined as unknown as Date,
      },
    };
  return {
    ...QUOTE_PRICING_POLICY_DEFAULT_VALUES,
  };
};

type InitialValuesFromPayloadOptions = {
  supplierTypes: Array<ISupplierTypeOptionItem>;
  availableBrandIds: Array<number>;
};

export const getInitialValuesFromPayload = (
  payload: IGetOneQuotePricingPolicy,
  { supplierTypes, availableBrandIds }: InitialValuesFromPayloadOptions,
): IGlobalQuotePricingFormData => {
  const supplierTypesLookup = supplierTypes.reduce(
    (acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as Record<string, ISupplierTypeOptionItem>,
  );
  const QUOTE_PRICING_POLICY_DEFAULT_VALUES: IGlobalQuotePricingFormData = {
    PRICING: {
      name: payload.name,
      vehicleTypeGroup: payload.vehicleTypeGroup.key,
      configurations: payload.sections.map((section) => {
        const sectionSupplierTypes = section.supplierTypes
          .map((supplierTypeId) => supplierTypesLookup[supplierTypeId]?.value)
          .filter(Boolean);
        const refinementDefaults: ReturnType<typeof getRefinementDefaults> =
          section.policies.length === 1
            ? {
                definition: null,
                refinementFlags: {
                  diameter: false,
                  season: false,
                  supplierType: false,
                  tyreType: false,
                },
              }
            : getRefinementDefaults(
                section,
                section.supplierTypes,
                supplierTypesLookup,
              );

        return {
          name: section.name,
          brands: section.brands
            .filter((brand) => availableBrandIds.includes(Number(brand.key)))
            .map((brand) => Number(brand.key)),
          configurationTypeValue:
            section.policies.length === 1
              ? section.policies[0].priceValue.c
              : 0,
          configurationType: section.configurationType,
          supplierTypes: sectionSupplierTypes,
          refinementFlags: refinementDefaults.refinementFlags,
          definition: refinementDefaults.definition,
        };
      }),
    },
    APPLICATION: {
      startDate: new Date(payload.formattedStartDate),
      endDate: payload.formattedEndDate
        ? new Date(payload.formattedEndDate)
        : null,
    },
  };
  return {
    ...QUOTE_PRICING_POLICY_DEFAULT_VALUES,
  };
};

export const getInitialValuesFromPayloadForDefault = (
  payload: IGetOneQuotePricingPolicy,
  { supplierTypes, availableBrandIds }: InitialValuesFromPayloadOptions,
  fetchedCustomers: ICustomer[],
): IGlobalDefaultQuotePricingFormData => {
  const supplierTypesLookup = supplierTypes.reduce(
    (acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    },
    {} as Record<string, ISupplierTypeOptionItem>,
  );
  const QUOTE_PRICING_POLICY_DEFAULT_VALUES: IGlobalDefaultQuotePricingFormData =
    {
      PRICING: {
        name: payload.name,
        vehicleTypeGroup: payload.vehicleTypeGroup.key,
        configurations: payload.sections.map((section) => {
          const sectionSupplierTypes = section.supplierTypes
            .map((supplierTypeId) => supplierTypesLookup[supplierTypeId]?.value)
            .filter(Boolean);
          const refinementDefaults: ReturnType<typeof getRefinementDefaults> =
            section.policies.length === 1
              ? {
                  definition: null,
                  refinementFlags: {
                    diameter: false,
                    season: false,
                    supplierType: false,
                    tyreType: false,
                  },
                }
              : getRefinementDefaults(
                  section,
                  section.supplierTypes,
                  supplierTypesLookup,
                );
          return {
            name: section.name,
            brands: section.brands
              .filter((brand) => availableBrandIds.includes(Number(brand.key)))
              .map((brand) => Number(brand.key)),
            configurationTypeValue:
              section.policies.length === 1
                ? section.policies[0].priceValue.c
                : 0,
            configurationType: section.configurationType,
            supplierTypes: sectionSupplierTypes,
            refinementFlags: refinementDefaults.refinementFlags,
            definition: refinementDefaults.definition,
          };
        }),
      },
      APPLICATION: {
        startDate: new Date(payload.formattedStartDate),
        endDate: payload.formattedEndDate
          ? new Date(payload.formattedEndDate)
          : null,
        customers: payload.criteria.map((criterion) => criterion.value),
        customersAutocomplete: fetchedCustomers.map((customer) => ({
          label: `${customer.name} (${customer.code})`,
          value: customer.id,
        })),
      },
    };
  return {
    ...QUOTE_PRICING_POLICY_DEFAULT_VALUES,
  };
};

function getRefinementDefaults(
  section: IQuotePricingSectionPayload,
  supplierTypeIds: Array<number>,
  supplierTypesLookup: Record<string, ISupplierTypeOptionItem>,
): Pick<IQuotePolicyItem, 'refinementFlags' | 'definition'> {
  const definition: IQuotePolicyItem['definition'] = { supplierTypes: {} };
  const refinementFlags: IQuotePolicyItem['refinementFlags'] = {
    diameter: false,
    season: false,
    supplierType: false,
    tyreType: false,
  };
  const uniqVehicleTypes = uniq(
    section.policies.map((item) => item.tyreType?.key).filter(Boolean),
  ) as Array<string>;
  const uniqSeasons = uniq(
    section.policies
      .map((item) => item.season?.name)
      .filter(Boolean) as Array<string>,
  );
  const groupedPolicyByRefinementKey = section.policies.reduce(
    (acc, policy) => {
      if (policy.season !== null) {
        refinementFlags.season = true;
      }
      if (policy.diameter !== null) {
        refinementFlags.diameter = true;
      }
      if (policy.tyreType !== null) {
        refinementFlags.tyreType = true;
      }
      if (policy.supplierType !== null) {
        refinementFlags.supplierType = true;
      }

      const diameterKey = policy.diameter
        ? `${policy.diameter.from}-${policy.diameter.operator}-${policy.diameter.to}`
        : NO_REFINEMENT_VALUE;
      const seasonKey = policy.season
        ? policy.season.name
        : NO_REFINEMENT_VALUE;
      const tyreTypeKey = policy.tyreType
        ? policy.tyreType.key
        : NO_REFINEMENT_VALUE;
      const supplierTypeKey = policy.supplierType
        ? policy.supplierType.key
        : NO_REFINEMENT_VALUE;
      const refinementKeys = [
        supplierTypeKey,
        diameterKey,
        seasonKey,
        tyreTypeKey,
      ];
      set(acc, refinementKeys, policy);
      return acc;
    },
    {} as Record<
      string,
      Record<
        string,
        Record<string, Record<string, (typeof section.policies)[0]>>
      >
    >,
  );
  const vehicleTypeKeys = [...uniqVehicleTypes, NO_REFINEMENT_VALUE];
  const seasonKeys = [NO_REFINEMENT_VALUE, ...uniqSeasons];
  const supplierTypeKeys = [...supplierTypeIds, NO_REFINEMENT_VALUE];
  supplierTypeKeys.forEach((supplierTypeId) => {
    const policiesBySupplierType = groupedPolicyByRefinementKey[supplierTypeId];
    if (!policiesBySupplierType) {
      return;
    }
    const supplierTypeCodename =
      supplierTypeId === NO_REFINEMENT_VALUE
        ? NO_REFINEMENT_VALUE
        : supplierTypesLookup[supplierTypeId].value;
    const diameters = Object.keys(policiesBySupplierType);
    definition['supplierTypes'][supplierTypeCodename] = { refinements: [] };
    diameters.forEach((diameterGroupKey, diameterIndex) => {
      const diameter =
        diameterGroupKey === NO_REFINEMENT_VALUE
          ? null
          : ({
              from: Number(diameterGroupKey.split('-')[0]),
              operator: diameterGroupKey.split('-')[1],
              to: Number(diameterGroupKey.split('-')[2]),
            } as IDiameter);
      definition['supplierTypes'][supplierTypeCodename].refinements.push({
        diameter: diameter,
        seasons: {},
      });
      seasonKeys.forEach((seasonKey) => {
        definition['supplierTypes'][supplierTypeCodename].refinements[
          diameterIndex
        ].seasons[seasonKey] = { tyreTypes: {} };
        vehicleTypeKeys.forEach((vehicleTypeKey) => {
          const value = get(groupedPolicyByRefinementKey, [
            supplierTypeId,
            diameterGroupKey,
            seasonKey,
            vehicleTypeKey,
          ])?.priceValue.c;
          definition['supplierTypes'][supplierTypeCodename].refinements[
            diameterIndex
          ].seasons[seasonKey]['tyreTypes'][vehicleTypeKey] = { value };
        });
      });
    });
  });
  return { definition, refinementFlags };
}

type InitialRefinementArgs = {
  flags: IQuotePolicyItem['refinementFlags'];
  seasons: Array<ISeasonOptionItem>;
  tyreTypes: Array<IOptionItem>;
  guessedDiameter?: {
    diameter?: number;
    operator?: IDiameter['operator'];
  };
};

type RefinementItem = NonNullable<
  IQuotePolicyItem['definition']
>['supplierTypes'][string]['refinements'][number];

export const getInitialRefinement = ({
  flags,
  seasons,
  tyreTypes,
  guessedDiameter,
}: InitialRefinementArgs): RefinementItem => {
  const defaultValue = undefined as unknown as 0;
  let defaultSeasons: RefinementItem['seasons'] = {
    ALL: { tyreTypes: { ALL: { value: defaultValue } } },
  };
  let defaultDiameter = null;
  if (flags.season) {
    defaultSeasons = {};
    seasons.forEach((season) => {
      defaultSeasons[season.label] = {
        tyreTypes: { ALL: { value: defaultValue } },
      };
      if (flags.tyreType) {
        defaultSeasons[season.label] = { tyreTypes: {} };
        tyreTypes.forEach((tyreType) => {
          defaultSeasons[season.label]['tyreTypes'][tyreType.value] = {
            value: defaultValue,
          };
        });
      }
    });
  }

  if (flags.tyreType) {
    // in this case we don't need to add the ALL key
    defaultSeasons = {};
  }
  if (flags.diameter) {
    // The assertion is load-bearing: `operator` is optional here but
    // required on IDiameter.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    defaultDiameter = {
      from: guessedDiameter?.diameter,
      to: undefined,
      operator: guessedDiameter?.operator,
    } as unknown as IDiameter;
  }
  return {
    diameter: defaultDiameter,
    seasons: defaultSeasons,
  };
};

export const getFieldSign = (
  configurationType: IQuotePolicyItem['configurationType'],
  currency?: string,
) => {
  if (configurationType === 'DISCOUNT_BF' || configurationType === 'MARGIN') {
    return '%';
  }
  return currency ? getCurrencySymbol(currency) : DEFAULT_EURO_SIGN;
};
